import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { IonActionSheet } from '@ionic/react';
import * as moment from 'moment';

import { ContextGlobal as CG } from 'contexts';
import { useConversionTable } from 'hooks/useConversionTable';
import { Button, Descr, Ruler, Subtitle } from 'components/page';
import * as Table from 'components/table';

import { useCopyToClipboard } from 'react-use';
import { FileCopy } from '@mui/icons-material';
import { ContractCard } from './ContractCard';
import { ContractReaudit } from './ContractReaudit';
import * as Styled from './StyledContract';

export function Contract(props) {
    const { emitAlert, state } = React.useContext(CG);

    const { t } = useTranslation('ranking');
    const conv = useConversionTable(['contractType', 'yesNo']);

    const [showAS, setShowAS] = React.useState(false);
    const [reauditing, setReauditing] = React.useState(false);
    const [startToEndDate, setStartToEndDate] = React.useState();
    const [copy, setCopy] = useCopyToClipboard();
    const {
        deleteUserContract,
        postUserContractValidation,
        postUserContractValidationEmail
    } = props.useRankingCorporate;

    const data = props.data || props.contract || undefined;

    const { contractInfo, startupContact } = data;

    // Moment language
    let lang = state.language;
    if (state.language === 'pt') {
        lang = 'pt-br';
    }

    const regDate = moment(data.regDate)
        .utc()
        .locale(lang)
        .format('DD MMM YYYY');

    const contractStartDate = moment(contractInfo.startDate)
        .utc()
        .locale(lang)
        .format('MMM YYYY');

    const contractEndDate = moment(contractInfo.endDate)
        .utc()
        .locale(lang)
        .format('MMM YYYY');

    const formalizedOption = contractInfo.formalized || false;
    const caseOption = contractInfo.caseApply || false;

    useEffect(() => {
        if (!data.startDate && !data.endDate) {
            setStartToEndDate('Vigência não declarada');
        } else {
            setStartToEndDate(
                t('dateRange', {
                    startDate: data.startDate ? contractStartDate : 'Data não declarada',
                    endDate: data.endDate ? contractEndDate : 'data não declarada'
                })
            );
        }
    }, [data.startDate, data.endDate]);

    let contract = {};

    if (startupContact && startupContact.name) {
        contract.contactName = startupContact?.name;
    }
    if (startupContact && startupContact.email) {
        contract.contactEmail = startupContact?.email;
    }
    contract.contractType = conv?.contractType[contractInfo.contractType];
    if (contractInfo && contractInfo.contractType === 'acquisition') {
        contract.contractType = `${t('StartupStep8.typeOldAcquisition')}`;
    }
    if (contractInfo && contractInfo.contractType === 'investment') {
        contract.contractType = `${t('StartupStep8.typeOldInvestment')}`;
    }
    contract = {
        ...contract,
        valueRange: contractInfo.valueRangeText || 'N/A',
        registrationDate: regDate,
        startToEndDate,
        formalized: conv.yesNo[String(formalizedOption)],
        caseApply: conv.yesNo[String(caseOption)]
    };

    if (data.year) {
        contract.year = '3';
    }

    // Properties for the table
    const propsTable = {
        object: {
            data: { ...contract },
            name: 'Contact',
            dataConv: {}
        }
    };

    const closeModal = () => {
        if (props.closeModal) props.closeModal();
        return true;
    };

    function handleConfirm() {
        const payload = {
            _id: data._id,
            status: 'confirmed',
            lastUpdated: data.lastUpdated,
            entity: 'company'
        };
        return postUserContractValidation(payload).then(closeModal);
    }
    function handleDelete() {
        const payload = { _id: data._id, entity: 'company' };
        return deleteUserContract(payload).then(res => {
            closeModal();
            emitAlert(t('contractDeleted'));
            return res;
        });
    }
    function handleReject() {
        const payload = {
            _id: data._id,
            status: 'rejected',
            entity: 'company',
            lastUpdated: data.lastUpdated
        };
        return postUserContractValidation(payload).then(closeModal);
    }
    function handleResend() {
        const payload = { _id: data._id, entity: 'company', startupId: data.startup._id };
        return postUserContractValidationEmail(payload).then(res => {
            emitAlert(t('contractResent'));
            return res;
        });
    }

    function clickReaudit() {
        setReauditing(true);
    }

    const buttons = [];
    if (
        data.status === 'pending' &&
        (data.startup.startupStatus === 'registered' || data.startup.startupStatus === 'none') &&
        data.startupStatus === 'pending'
    ) {
        const r = { type: 'dark', onClick: handleResend };
        buttons.push(() => (
            <Styled.ContractButtons>
                <Button {...r}>{t('glossary:resend')}</Button>
            </Styled.ContractButtons>
        ));
    }
    if (data.status === 'pending' && data.companyStatus === 'pending') {
        const s = { type: 'success', onClick: handleConfirm };
        const d = { type: 'danger', onClick: handleReject };
        buttons.push(() => (
            <Styled.ContractButtons>
                <Button {...s}>{t('glossary:accept')}</Button>
                <Button {...d}>{t('glossary:reject')}</Button>
            </Styled.ContractButtons>
        ));
    }
    if (data?.status === 'pending' && data.startupStatus === 'pending') {
        const l = { type: 'link', onClick: () => setShowAS(true) };
        buttons.push(() => <Button {...l}>{t('contractDelete')}</Button>);
    }

    if (
        !reauditing &&
        data.audited === 'rejected' &&
        data.displayAudit &&
        !data.reconsiderationResult
    ) {
        buttons.push(() => (
            <Button type="dark" onClick={clickReaudit}>
                <span className="text">{t('glossary:reaudit')}</span>
                <span className="icon far fa-long-arrow-right" aria-hidden="true" />
            </Button>
        ));
    }

    return (
        <Styled.Contract className={props.className} style={props.style}>
            <ContractCard data={data} fromContract />

            <Table.Table {...propsTable} />
            <Styled.ContractIdContainer>
                <span>
                    {data._id}
                    <Styled.CopyButton
                        type="dark"
                        variant="neutral"
                        onClick={() => setCopy(data._id)}
                    >
                        <FileCopy> </FileCopy>
                        {!copy.value && <span className="text"> </span>}
                        {!!copy.value && <span className="text"> </span>}
                    </Styled.CopyButton>
                </span>
                <p>ID</p>
            </Styled.ContractIdContainer>
            {contractInfo.description && <Descr>{contractInfo.description}</Descr>}

            {data.displayAudit && data.auditComment && (
                <>
                    <Ruler />
                    <Subtitle text={t('auditComment')} />
                    <Descr>{data.auditComment}</Descr>
                </>
            )}

            <ContractReaudit
                data={data}
                lang={lang}
                reauditing={reauditing}
                setReauditing={setReauditing}
                useRankingCorporate={props.useRankingCorporate}
            />

            {buttons.map((Btn, k) => (
                // eslint-disable-next-line react/no-array-index-key
                <Btn key={`button-${k}`} />
            ))}

            <IonActionSheet
                isOpen={showAS}
                onDidDismiss={() => setShowAS(false)}
                buttons={[
                    {
                        text: t('contractDelete'),
                        role: 'destructive',
                        handler: handleDelete
                    },
                    {
                        text: t('glossary:cancel'),
                        role: 'cancel'
                    }
                ]}
            />
        </Styled.Contract>
    );
}
export default Contract;
